import { produce } from "immer";
import { AnyAction } from "redux";

import { IResponseStatus, MessageResponse } from "@/_helpers/iResponse";
import { convertKeysToCamelCase } from "@/_helpers/stringHelper";
import { IBuilding } from "@/modules/building/reducers";
import {
  GET_DWELLINGS_SUCCESS,
  GET_DWELLING_SUCCESS,
  GET_DWELLING_SERVICES_SUCCESS,
  GET_DWELLING_SERVICES_INVENTORY_SUCCESS,
} from "@/modules/dwelling/constants/index.ts";
import {
  IDwellingAddress,
  IDwellingEquipment,
} from "@/modules/dwelling/interfaces/index.ts";
import { ICustomer } from "@/modules/profile/interfaces/customer.ts";
import { IReferential } from "@/modules/referential/reducers";
import { IKeyServiceOrdering } from "@/modules/serviceOrdering/interfaces";
import { Meter } from "@/modules/serviceOrdering/interfaces/ServiceReport";
import { IService, ITenant, IFile } from "@/modules/serviceOrdering/reducers";

export enum ConstructionPeriod {
  BEFORE_1949 = "BEFORE_1949",
  AFTER_1949 = "AFTER_1949",
  BETWEEN_1949_AND_1997 = "BETWEEN_1949_AND_1997",
  AFTER_1997 = "AFTER_1997",
  AFTER_2005 = "AFTER_2005",
  AFTER_2022 = "AFTER_2022",
  UNKNOWN = "UNKNOWN",
  DWECP_UNKNOWN = "DWECP_UNKNOWN",
}

export enum ConstructionPeriodLabel {
  BEFORE_1949 = "Avant 1949",
  AFTER_1949 = "Après 1949",
  BETWEEN_1949_AND_1997 = "Entre 1949 et 1997",
  AFTER_1997 = "Après 1997",
  AFTER_2005 = "Après 2005",
  AFTER_2022 = "Après 2022",
  UNKNOWN = "Inconnue",
  DWECP_UNKNOWN = "Période inconnue",
}

export interface IDwelling {
  id: string;
  ademeNumber?: string;
  area?: string;
  category: IReferential;
  comment?: string;
  condition?: IReferential;
  linkVirtualVisit?: string;
  condominiumNumber?: string;
  condominiumSyndicate?: string;
  constructionPeriod: IReferential;
  constructionDate?: string;
  datedReference: string;
  isCondominium?: boolean;
  isFurnished: boolean;
  isSociety: boolean;
  lessorDesignation: boolean;
  lessorFirstname?: string;
  lessorName: string;
  lessor: Lessor | null;
  managementLotNumber?: string;
  taxNumber?: string;
  type?: IReferential;
  address: IDwellingAddress;
  building?: IBuilding;
  buildingId?: string;
  equipments: IDwellingEquipment[];
  files?: any[];
  services?: IService[];
  tenants?: ITenant[];
  lastService?: IService;
  mainFile?: IFile;
  lastServiceInventory?: IService;
  lastServiceInventoryReport?: Report;
  incomingTenants?: ITenant[];
  outgoingTenants?: ITenant[];
  currentTenants?: ITenant[];
  previousTenants?: ITenant[];
  agency: IServiceAgency;
  key: IKeyServiceOrdering;
  goodCondition?: Report;
  cleanliness?: Report;
  lastMeasurementDate?: Meter;
  dpe?: string;
  ges?: string;
  permissions: string[];
  agencyId: string;
  lessorId?: string;
  addressLabel: string;
  diagnostics: DiagnosticReferentialData;
  [key: string]: ITenant[] | any;
  buildingNumber?: string;
  batchNumber?: string;
}

export const initialDwelling: IDwelling = {
  id: "",
  category: { value: "", label: "" },
  constructionPeriod: { value: "", label: "" },
  isFurnished: false,
  isSociety: false,
  lessorDesignation: false,
  lessorName: "",
  lessor: null,
  address: {
    address: {
      additionalAddress: "",
      address: "",
      city: "",
      latitudeY: "",
      longitudeX: "",
      zip: "",
    },
  },
  equipments: [],
  agency: {
    id: "",
    label: "",
    manageErpBillings: false,
    address: {
      additionalAddress: "",
      address: "",
      city: "",
      latitudeY: "",
      longitudeX: "",
      zip: "",
    },
    customer: {
      id: "",
    },
  },
  key: {},
  permissions: [],
  agencyId: "",
  addressLabel: "",
  diagnostics: {},
  datedReference: "",
};

export interface DiagnosticReferentialData {
  //DPE
  EPD?: {
    energy: {
      class: string;
      consumption: string;
      interventionDate: string | null;
      validityDate?: string;
    };
    ghg: {
      class: string;
      consumption: string;
      interventionDate: string | null;
      validityDate?: string;
    };
    //Worst of ghg and energy class
    EPDLabel2020?: string;
  };
  //Amiante
  asbestos?: {
    isPresent: boolean;
    interventionDate: string | null;
    validityDate?: string;
  };
  //Plomb
  lead?: {
    isPresent: boolean;
    interventionDate: string | null;
    validityDate?: string;
  };
  //Surface
  area?: {
    living: number;
    carrez: number;
  };
  electricity?: {
    //Électricité
    hasIssue: boolean;
    interventionDate: string | null;
    validityDate?: string;
  };
  gas?: {
    //Gaz
    hasIssue: boolean;
    interventionDate: string | null;
    validityDate?: string;
  };
  //Termites
  termite?: {
    isPresent: boolean;
    interventionDate: string | null;
    validityDate?: string;
  };
  files?: IFile;
}

export interface IDwellingExpress {
  address: {
    additionalAddress?: string;
    city: string;
    address: string;
    latitudeY: string;
    longitudeX: string;
    zip: string;
    digicode?: string;
    residenceName?: string;
  };
  category: {
    value: string;
    label: string;
  };
  type?: {
    value: string;
    label: string;
  };
  constructionPeriod: {
    label: string;
    value: string;
  };
  isSociety: boolean;
  isFurnished: boolean;
  isCondominium: boolean;
  lessorFirstname: string;
  area: string;
  lessorName: string;
  condominiumNumber?: string;
  taxNumber?: string;
  ademeNumber?: string;
  buildingNumber?: string;
  batchNumber?: string;
}

export interface IServiceAgency {
  id: string;
  label: string;
  manageErpBillings: boolean;
  address: {
    additionalAddress: string;
    address: string;
    city: string;
    latitudeY: string;
    longitudeX: string;
    zip: string;
  };
  customer: ICustomer;
}

export interface Lessor {
  id: string;
  name: string;
  firstname?: string;
  isCompany: boolean;
  customer?: ICustomer;
}

export interface IResponseDwellings {
  dwellings: Array<IDwelling>;
  count: number;
  currentPage: number;
}

export interface IResponseServices {
  services: Array<IService>;
  count: number;
  currentPage: number;
}

export interface IResponseDwelling extends IResponseStatus {
  data: {
    data?: {
      id?: string;
      count?: number;
      dwelling?: IDwelling;
      dwellings?: Array<IDwelling>;
      services?: Array<IService>;
    };
    message: MessageResponse;
  };
}

export interface IResponsePaginateServices {
  dwellings: Array<IDwelling>;
  perPage: number;
  currentPage: number;
  firstPage: number;
  total?: number;
  lastPage: number;
}

const initState = {
  dwelling: null,
  dwellings: null,
  dwellingsCount: null,
  dwellingServices: null,
  dwellingServicesCount: null,
  dwellingServicesInventory: null,
  dwellingServicesInventoryCount: null,
};

const dwellingReducer = produce((draft = initState, action: AnyAction) => {
  switch (action.type) {
    case GET_DWELLINGS_SUCCESS:
      draft.dwellings = action.payload.dwellings;
      draft.dwellingsCount = action.payload.count;
      break;
    case GET_DWELLING_SUCCESS:
      draft.dwelling = convertKeysToCamelCase(action.payload);
      break;
    case GET_DWELLING_SERVICES_SUCCESS:
      draft.dwellingServices = convertKeysToCamelCase(action.payload.services);
      draft.dwellingServicesCount = action.payload.count;
      break;
    case GET_DWELLING_SERVICES_INVENTORY_SUCCESS:
      draft.dwellingServicesInventory = convertKeysToCamelCase(
        action.payload.services
      );
      draft.dwellingServicesInventoryCount = action.payload.count;
      break;
    default:
      return draft;
  }
});

export default dwellingReducer;
